import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../lib/supabaseClient';

async function fetchAccount(userId) {
  const { data: household, error: householdError } = await supabase
    .from('households')
    .select('id')
    .eq('owner_id', userId)
    .maybeSingle();

  if (householdError) throw householdError;

  if (household) {
    const { data: children, error: childrenError } = await supabase
      .from('children')
      .select('id')
      .eq('household_id', household.id)
      .limit(1);

    if (childrenError) throw childrenError;
    if (!children?.length) {
      return {
        status: 'needs-setup',
        householdId: household.id,
        role: 'parent',
        studentChild: null
      };
    }

    return {
      status: 'ready',
      householdId: household.id,
      role: 'parent',
      studentChild: null
    };
  }

  // Linked student session (Switch to Student) — no owned household.
  const { data: ownChild, error: ownChildError } = await supabase
    .from('children')
    .select('id, name, grade')
    .eq('auth_user_id', userId)
    .maybeSingle();

  if (ownChildError) throw ownChildError;
  if (ownChild) {
    return {
      status: 'ready',
      householdId: null,
      role: 'student',
      studentChild: ownChild
    };
  }

  return { status: 'needs-setup', householdId: null, role: 'parent' };
}

function useAppAuth() {
  const [session, setSession] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [accountStatus, setAccountStatus] = useState('loading');
  const [accountRole, setAccountRole] = useState(null);
  const [householdId, setHouseholdId] = useState(null);
  const [studentChild, setStudentChild] = useState(null);
  const [accountError, setAccountError] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    let cancelled = false;

    supabase.auth.getSession().then(({ data: { session: nextSession } }) => {
      if (cancelled) return;
      setSession(nextSession);
      setAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, nextSession) => {
        setSession(nextSession);
        setAuthReady(true);
        if (nextSession) setAccountStatus('loading');
      }
    );

    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!authReady) return;

    if (!session) {
      setAccountStatus('anonymous');
      setAccountRole(null);
      setHouseholdId(null);
      setStudentChild(null);
      setAccountError(null);
      return;
    }

    let cancelled = false;
    setAccountStatus('loading');
    setAccountError(null);

    fetchAccount(session.user.id)
      .then((result) => {
        if (cancelled) return;
        setHouseholdId(result.householdId);
        setAccountRole(result.role);
        setStudentChild(result.studentChild || null);
        setAccountStatus(result.status);
      })
      .catch((error) => {
        if (cancelled) return;
        setAccountError(error.message || 'Could not load your household.');
        setAccountStatus('error');
        setAccountRole(null);
        setStudentChild(null);
      });

    return () => {
      cancelled = true;
    };
  }, [session, authReady, refreshTick]);

  const refreshAccount = useCallback(() => {
    setRefreshTick((current) => current + 1);
  }, []);

  return {
    session,
    authReady,
    accountStatus,
    accountRole,
    householdId,
    studentChild,
    accountError,
    refreshAccount
  };
}

export default useAppAuth;
